import { formatNumber } from "../../utils/FormattingUtils";
import {
  applyTransformations,
  registerTransformation,
  TransformationType,
} from "../calculation/Calculation";
import { TransformationTags } from "../calculation/TransformationTags";
import { buildEventMessage } from "../events/EventMessageBuilder";
import {
  EventOccurrenceData,
  getEventOccurrenceById,
  triggerEvent,
} from "../events/Events";
import { createGameEvent } from "../events/SimpleGameEvent";
import { clearFlag, hasFlag, hasGlobalFlag, setGlobalFlag } from "../Flags";
import { PlayerContextState } from "../PlayerContext";
import { getResourceAmount, grantResource, Resource } from "../Resources";
import { OfflineTickListener } from "./OfflineTickListeners";
import { registerTimeTickListener } from "./TimeTick";

const timePieceEvent = createGameEvent(
  "TimePieceEvent",
  buildEventMessage("TimePieceEvent", "Offline Progress")
    .setSimpleDescription(
      `Welcome back to Magic Research!
      
**You obtained {amount}:timepiece: Time Pieces!**`,
    )
    .build(),
  [],
  (state, _params) => state,
);

const timePieceEventFirstTime = createGameEvent(
  "TimePieceEventFirstTime",
  buildEventMessage("TimePieceEventFirstTime", "Offline Progress")
    .setSimpleDescription(
      `Welcome back to Magic Research!
      
**You obtained {amount}:timepiece: Time Pieces! Time Pieces allow you to make time pass faster for a duration. You can do so from the left side menu!**`,
    )
    .build(),
  [],
  setGlobalFlag("has_seen_offline_progress"),
);

export const timePieceGranter = new OfflineTickListener(
  "TimePieceGranter",
  (totalSecs) => {
    return (state) => {
      const transformedSecs = applyTransformations(
        [TransformationTags.OfflineTimePieceGain, Resource.TimePieces],
        state,
        totalSecs,
      );
      const oldTimePieces = getResourceAmount(state, Resource.TimePieces);
      // console.log("Granting time pieces", transformedSecs);
      state = grantResource(Resource.TimePieces, transformedSecs)(state);
      const newTimePieces = getResourceAmount(state, Resource.TimePieces);
      const amount = newTimePieces - oldTimePieces;
      if (amount > 0) {
        if (hasGlobalFlag(state, "has_seen_offline_progress")) {
          const lastEventOccurrenceId = state.eventQueue.find(
            (eventOccurrenceId) =>
              getEventOccurrenceById(state, eventOccurrenceId)?.eventId ===
              timePieceEvent.getId(),
          );
          if (lastEventOccurrenceId != null) {
            const eventOccurrence = getEventOccurrenceById(
              state,
              lastEventOccurrenceId,
            ) as EventOccurrenceData;
            const newAmount =
              amount + (eventOccurrence?.params?.rawAmount ?? 0);
            eventOccurrence.params.amount = formatNumber(newAmount);
            eventOccurrence.params.rawAmount = newAmount;
            state.eventOccurrences[lastEventOccurrenceId] = eventOccurrence;
          } else {
            if (amount > 60) {
              state = triggerEvent(timePieceEvent, {
                amount: formatNumber(amount),
                rawAmount: amount,
              })(state);
            }
          }
        } else {
          state = triggerEvent(timePieceEventFirstTime, {
            amount: formatNumber(amount),
            rawAmount: amount,
          })(state);
        }
      }
      return state;
    };
  },
);

registerTransformation(
  [[TransformationTags.GameSpeed]],
  "TimePieceGameSpeed",
  TransformationType.Multiplier,
  (state, params) =>
    !params.isWarp ? getCurrentTimePieceSpeedupSpeed(state) : 1,
);

registerTransformation(
  [[TransformationTags.GameSpeed]],
  "Debug2xGameSpeed",
  TransformationType.Multiplier,
  (state, params) =>
    hasFlag(state, "debug_2x_speed") && !params?.isWarp ? 2 : 1,
);

registerTransformation(
  [[TransformationTags.GameSpeed]],
  "Debug0xGameSpeed",
  TransformationType.Multiplier,
  (state, params) =>
    hasFlag(state, "debug_0x_speed") && !params?.isWarp ? 0 : 1,
);

export function getCurrentTimePieceSpeedupSpeed(state: PlayerContextState) {
  return hasFlag(state, "time_piece_8x_speedup_enabled")
    ? 8
    : hasFlag(state, "time_piece_speedup_enabled")
    ? 2
    : 1;
}

// Returns cost per extra second gained.
export function getCurrentTimePieceSpeedupCost(state: PlayerContextState) {
  if (hasFlag(state, "time_piece_8x_speedup_enabled")) {
    return applyTransformations(
      [TransformationTags.TimePiecePerFasterSpeedup],
      state,
      4.0,
    );
  } else if (hasFlag(state, "time_piece_speedup_enabled")) {
    return applyTransformations(
      [TransformationTags.TimePiecePerFastSpeedup],
      state,
      3.0,
    );
  }

  return 0;
}

// Returns cost per real-life second.
export function getCurrentTimePieceSpeedupCostPerRealSecond(
  state: PlayerContextState,
) {
  const speed = getCurrentTimePieceSpeedupSpeed(state);
  if (speed > 1) {
    const cost = getCurrentTimePieceSpeedupCost(state);
    return (speed - 1) * cost;
  }
  return 0;
}

function getCurrentTimePieceSpeedupCostPerGameSecond(
  state: PlayerContextState,
) {
  const speed = getCurrentTimePieceSpeedupSpeed(state);
  if (speed > 1) {
    const cost = getCurrentTimePieceSpeedupCost(state);
    return ((speed - 1) * cost) / speed;
  }
  return 0;
}

registerTimeTickListener(
  "TimePieceConsumer",
  (state, timeElapsedSec, isWarp) => {
    if (!isWarp) {
      const costPerSecond = getCurrentTimePieceSpeedupCostPerGameSecond(state);
      if (costPerSecond > 0) {
        state = grantResource(
          Resource.TimePieces,
          -costPerSecond * timeElapsedSec,
        )(state);
      }
      if (getResourceAmount(state, Resource.TimePieces) <= 0) {
        state = clearFlag("time_piece_8x_speedup_enabled")(state);
        state = clearFlag("time_piece_speedup_enabled")(state);
      }
    }
    return state;
  },
);
